import { useNavigate } from 'react-router-dom';
import { Check, Star, Zap, Shield, FileText, AlertCircle, Clock, Search, Map, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import SeoHead from '@/components/shared/SeoHead';
import PageHero, { HeroAccent } from '@/components/shared/PageHero';
import NewsletterSignup from '@/components/shared/NewsletterSignup';
import { useAnalytics } from '@/hooks/useAnalytics';

// Everything on Wage Atlas is free: all salary data, percentiles, real-wage
// adjustments, history, tools, and PDF reports (delivered by email). There are
// no paid products; the site is supported by ads and partner referrals.
const Pricing = () => {
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const freeFeatures = [
    'Unlimited salary searches & page views',
    'Full percentiles (P10-P90), not just the median',
    'Cost-of-living adjusted "real wages"',
    'Complete history back to 1997',
    'Interactive maps, trends & comparisons',
    'No-degree & growth/AI-risk insights',
  ];

  const faqItems = [
    {
      question: 'Is Wage Atlas really free?',
      answer: 'Yes. All salary data, percentiles, cost-of-living adjustments, historical trends, maps and career tools are 100% free. Wage Atlas is supported by advertising and partner referrals, so you never hit a paywall.',
    },
    {
      question: 'Are the PDF reports free too?',
      answer: 'Yes. The full salary and AI Replacement Risk PDF report - percentiles, location comparison, growth outlook and AI-risk for one occupation and location - is completely free. Just enter your email on any salary page and we deliver it to your inbox. It is handy for negotiations, applications and research.',
    },
    {
      question: 'What data sources do you use?',
      answer: 'Official Bureau of Labor Statistics (BLS) OEWS data covering 800+ occupations across every US state and metro area, cost-of-living adjusted using BEA Regional Price Parities, plus O*NET skills and BLS employment projections.',
    },
    {
      question: 'Do I need an account?',
      answer: 'No. There is nothing to sign up for - every page, tool and report is open to everyone. If you request a free PDF report, you\'ll also receive our salary insights newsletter, which you can unsubscribe from anytime.',
    },
  ];

  return (
    <>
      <SeoHead
        title="Pricing - Everything Is 100% Free | Wage Atlas"
        description="All Wage Atlas salary data, percentiles, real-wage adjustments, career tools and PDF reports are completely free. No subscriptions, no paywalls, no accounts."
        canonical="https://wageatlas.com/pricing/"
      />

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <PageHero
          badge={{ icon: Star, label: '100% free, powered by official BLS data' }}
          title={
            <>
              Everything is
              <HeroAccent> free</HeroAccent>
            </>
          }
          subtitle="No subscriptions, no paywalls, no view limits. Just real salary data for every job and place in America."
        >
          <Button
            size="lg"
            className="bg-white text-primary hover:bg-gray-100"
            onClick={() => navigate('/search')}
          >
            <Search className="w-5 h-5 mr-2" />
            Start exploring salaries
          </Button>
        </PageHero>

        {/* Free + Report cards */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Free */}
              <Card className="relative border-primary shadow-lg">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-white">Everyone</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-center">
                    <div className="mb-2">Free Forever</div>
                    <div className="text-4xl font-bold text-primary">$0</div>
                    <div className="text-sm text-muted-foreground">No account required</div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {freeFeatures.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mr-3 mt-1 flex-shrink-0" />
                        <span className="text-sm font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" onClick={() => navigate('/search')}>
                    Start exploring
                  </Button>
                </CardContent>
              </Card>

              {/* Free PDF report */}
              <Card className="relative">
                <CardHeader>
                  <CardTitle className="text-center">
                    <div className="mb-2">PDF Report</div>
                    <div className="text-4xl font-bold">
                      Free
                      <span className="text-lg text-muted-foreground"> by email</span>
                    </div>
                    <div className="text-sm text-muted-foreground">Delivered to your inbox</div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <FileText className="w-4 h-4 text-primary mr-3 mt-1 flex-shrink-0" />
                      <span className="text-sm">Polished, shareable salary report for one job + location</span>
                    </li>
                    <li className="flex items-start">
                      <TrendingUp className="w-4 h-4 text-primary mr-3 mt-1 flex-shrink-0" />
                      <span className="text-sm">Percentiles, growth outlook & AI-risk in one document</span>
                    </li>
                    <li className="flex items-start">
                      <Map className="w-4 h-4 text-primary mr-3 mt-1 flex-shrink-0" />
                      <span className="text-sm">Great for negotiations, applications & research</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">Just enter your email - no payment, no account</span>
                    </li>
                  </ul>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      analytics.trackAIAnalysisCTAClick('pricing_free_report_cta', undefined, false);
                      navigate('/search');
                    }}
                  >
                    Find a job to report on
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* AI Analysis Feature Card */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">AI-Powered Career Insights</h2>
              <p className="text-xl text-muted-foreground">
                Understand your career's future in the age of AI - free
              </p>
            </div>

            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="p-4 sm:p-8">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <Zap className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">AI Replacement Risk Analysis</h3>
                        <p className="text-muted-foreground">Free for every visitor</p>
                      </div>
                    </div>

                    <div className="space-y-4 mb-6">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold mb-1">Risk Scoring</h4>
                          <p className="text-sm text-muted-foreground">
                            A 0-100% risk score based on occupation characteristics, automation trends, and industry analysis
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold mb-1">Timeline Estimates</h4>
                          <p className="text-sm text-muted-foreground">
                            When automation might impact your field: near-term (1-3 yrs), medium-term (3-7 yrs), long-term (7-15 yrs)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold mb-1">Adaptation Strategies</h4>
                          <p className="text-sm text-muted-foreground">
                            Personalized recommendations for skills development and career transitions based on your risk profile
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => {
                        analytics.trackAIAnalysisCTAClick('pricing_learn_more', undefined, false);
                        navigate('/ai-career-analysis');
                      }}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Try the AI Risk Analysis
                    </Button>
                  </div>

                  <div className="relative">
                    <div className="p-6 bg-background border rounded-lg space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">AI Risk Score</span>
                          <Badge variant="secondary">Moderate</Badge>
                        </div>
                        <div className="w-full bg-muted rounded-full h-6 relative overflow-hidden">
                          <div className="h-full bg-yellow-500 rounded-full" style={{ width: '45%' }} />
                          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
                            45% Risk
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-muted rounded">
                          <div className="font-medium">Near-term</div>
                          <Badge variant="outline" className="mt-1">Low</Badge>
                        </div>
                        <div className="text-center p-2 bg-muted rounded">
                          <div className="font-medium">Medium-term</div>
                          <Badge variant="outline" className="mt-1">Moderate</Badge>
                        </div>
                        <div className="text-center p-2 bg-muted rounded">
                          <div className="font-medium">Long-term</div>
                          <Badge variant="outline" className="mt-1">Moderate</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-2xl">
            <NewsletterSignup
              source="pricing_page"
              title="Get Salary Insights in Your Inbox"
              description="Wage trends, career insights and job market analysis - free, unsubscribe anytime."
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 text-center">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold mb-4">No catch. Just data.</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Find what any job pays, where it pays the most, and where your paycheck goes furthest.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90" onClick={() => navigate('/search')}>
              <Search className="w-5 h-5 mr-2" />
              Explore salaries free
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing;
